import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dedicated auth axios instance — never shares global headers
const authAxios = axios.create({ baseURL: API });
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [licenseAccess, setLicenseAccess] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      const response = await authAxios.get('/auth/me');
      const userData = response.data;

      // Guard: verify token still belongs to the same user
      const storedEmail = localStorage.getItem('auth_user_email');
      if (storedEmail && userData.email !== storedEmail) {
        console.warn('Session mismatch detected — another user logged in');
        logout();
        return;
      }

      setUser(userData);
      localStorage.setItem('auth_user_email', userData.email);

      try {
        const licRes = await authAxios.get('/license/access-status');
        setLicenseAccess(licRes.data);
      } catch {
        setLicenseAccess({ has_access: true, reason: 'error', card_added: false, trial_days_remaining: 0 });
      }
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  // Cross-tab session detection — log out if another tab changes the token
  useEffect(() => {
    const onStorageChange = (e) => {
      if (e.key === 'token') {
        if (!e.newValue) {
          // Token removed in another tab → logout here too
          setToken(null);
          setUser(null);
        } else if (e.newValue !== token) {
          // Different token set in another tab → verify and reload
          setToken(e.newValue);
        }
      }
      if (e.key === 'auth_user_email' && e.newValue && user && e.newValue !== user.email) {
        // Another user logged in from a different tab
        console.warn('Another user logged in from a different tab');
        setToken(localStorage.getItem('token'));
      }
    };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, [token, user]);

  const login = async (email, password) => {
    const response = await authAxios.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('auth_user_email', userData.email);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, extraData = {}) => {
    const response = await authAxios.post('/auth/register', { email, password, name, ...extraData });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('auth_user_email', userData.email);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('auth_user_email');
    // NEVER use axios.defaults.headers.common — it's global and shared
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, licenseAccess, login, register, logout, fetchUser, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
